import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, History, LogOut, Users, ShieldCheck, Sparkles, LayoutDashboard, FileText } from "lucide-react";
import { apiFetch, getToken } from "@/api";

const UserAvatar = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!getToken()) return;
    apiFetch("/me")
      .then(setUser)
      .catch(() => {});
  }, []);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  if (!user) return null;

  const initial = (user.name || "S").trim()[0].toUpperCase();

  const signOut = (dest) => {
    apiFetch("/auth/logout", { method: "POST" }).catch(() => {});
    localStorage.removeItem("abhyas_token");
    localStorage.removeItem("abhyas_profile");
    window.location.href = dest;
  };

  const itemClass =
    "flex w-full items-center gap-2.5 rounded-xl px-4 py-2.5 text-left text-sm font-bold text-[#17201A] transition-colors duration-200 hover:bg-[#F8FAF7] hover:text-[#16A34A]";

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="user-avatar-btn"
        onClick={() => setOpen((o) => !o)}
        aria-label="Account menu"
        className="flex h-9 w-9 items-center justify-center rounded-full bg-[#16A34A] font-display text-sm font-extrabold text-white transition-transform duration-200"
      >
        {initial}
      </button>
      {open && (
        <div
          data-testid="avatar-dropdown"
          className="animate-fade-up absolute right-0 top-12 z-50 w-64 rounded-2xl border border-[#E3ECE6] bg-white p-2 shadow-lg"
        >
          <div className="border-b border-[#E3ECE6] px-4 py-3">
            <p
              data-testid="avatar-name"
              className="font-display text-sm font-extrabold text-[#17201A]"
            >
              {user.name}
            </p>
            <p
              data-testid="avatar-contact"
              className="mt-0.5 text-xs font-semibold text-[#647067]"
            >
              {user.email || `+91 ${user.phone}`}
            </p>
          </div>
          <div
            data-testid="billing-section"
            className="border-b border-[#E3ECE6] px-4 py-3"
          >
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#647067]">
              Billing
            </p>
            {user.planStatus === "active" && user.plan ? (
              <p
                data-testid="billing-plan"
                className="mt-1 text-xs font-bold text-[#16A34A]"
              >
                {user.plan} plan · Renews{" "}
                {new Date(user.planRenewsAt).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </p>
            ) : user.weekPassActive ? (
              <p data-testid="billing-plan" className="mt-1 text-xs font-bold text-[#16A34A]">
                7-Day Pass · active
              </p>
            ) : (
              <p
                data-testid="billing-plan"
                className="mt-1 text-xs font-bold text-[#647067]"
              >
                Free plan
              </p>
            )}
          </div>
          <div className="py-1.5">
            <button
              data-testid="menu-dashboard"
              onClick={() => navigate("/dashboard")}
              className={itemClass}
            >
              <LayoutDashboard className="h-4 w-4 text-[#16A34A]" />
              Dashboard
            </button>
            <button
              data-testid="menu-profile"
              onClick={() => navigate("/dashboard")}
              className={itemClass}
            >
              <User className="h-4 w-4 text-[#16A34A]" />
              Profile
            </button>
            <button
              data-testid="menu-practice-history"
              onClick={() => navigate("/progress")}
              className={itemClass}
            >
              <History className="h-4 w-4 text-[#16A34A]" />
              Practice History
            </button>
            <button
              data-testid="menu-pyq"
              onClick={() => navigate("/pyq")}
              className={itemClass}
            >
              <FileText className="h-4 w-4 text-[#16A34A]" />
              PYQ Papers
            </button>
            {user.isAdmin && (
              <button
                data-testid="menu-admin"
                onClick={() => navigate("/admin")}
                className={itemClass}
              >
                <ShieldCheck className="h-4 w-4 text-[#16A34A]" />
                Admin Dashboard
              </button>
            )}
            <button
              data-testid="menu-abhyas-ai"
              onClick={() => navigate("/abhyas-ai")}
              className={itemClass}
            >
              <Sparkles className="h-4 w-4 text-[#16A34A]" />
              Abhyas AI
            </button>
            <button
              data-testid="menu-switch-account"
              onClick={() => signOut("/login")}
              className={itemClass}
            >
              <Users className="h-4 w-4 text-[#16A34A]" />
              Switch Account
            </button>
            <button
              data-testid="menu-sign-out"
              onClick={() => signOut("/")}
              className={itemClass}
            >
              <LogOut className="h-4 w-4 text-[#16A34A]" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAvatar;
